/**
 * Componente Footer Adaptado ao Design
 * Mostra:
 * - Seção superior com informações, mapa do site e links úteis
 * - Seção inferior com logos de parceiros (dados reais do CMS)
 * - Responsivo para desktop e mobile
 * - Mantém mapper, types e query inalterados
 */

'use client';

import Link from "next/link";
import Image from "next/legacy/image";
import { IFooter } from "@/services/types";
import { Marquee } from "@/components/atoms/marque";

const Footer = ({
  logo,
  social_midia,
  contact,
  site_map,
  site_map_title,
  links_title,
  button_kre_mais,
  cms_parceiros,
}: IFooter) => {
  // Debug: verificar dados dos parceiros
  console.log('cms_parceiros:', cms_parceiros);

  // Função auxiliar para normalizar URLs
  const normalizeUrl = (url: string): string => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const linkHoverStyle = "transition-all duration-300 hover:text-greeniefp hover:translate-x-1";

  return (
    <footer className="relative w-full">
      {/* Seção Principal do Footer */}
      <div className="w-full bg-custom-gradient-footer">
        <div className="w-full h-auto min-h-[500px] lg:min-h-[600px] py-8 md:py-16 lg:py-20 text-white flex flex-col justify-between">

          {/* Container Principal - Conteúdo do Footer */}
          <div className="container mx-auto px-4 md:px-6 lg:px-8">

            {/* Grid com 3 colunas principais */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8 md:gap-12 lg:gap-16 mb-12 md:mb-16">

              {/* ========== COLUNA 1: LOGO E INFORMAÇÕES ========== */}
              <div className="flex flex-col">
                {/* Logo */}
                <div className="mb-8">
                  <Link href="/">
                    {logo?.url ? (
                      <Image
                        src={logo.url}
                        width={198}
                        height={96}
                        alt={logo.alternativeText || "CERMI Logo"}
                        className="object-contain w-[160px] md:w-[198px] h-auto"
                        priority
                      />
                    ) : (
                      <div className="w-[198px] h-[96px] bg-white/10 flex items-center justify-center text-white rounded">
                        Logo
                      </div>
                    )}
                  </Link>
                </div>

                {/* Redes Sociais - Mobile (logo após a logo) */}
                <div className="mb-8 md:hidden">
                  <div className="flex gap-4">
                    {social_midia?.map((item, index) => (
                      item?.logo?.url && (
                        <a
                          key={index}
                          href={normalizeUrl(item.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={item.name}
                          className="transition-transform duration-300 hover:scale-110 flex justify-center items-center border border-white/30 rounded-full py-3 px-3 hover:border-white/60"
                        >
                          <Image
                            src={item.logo.url}
                            alt={item?.name || 'Social media'}
                            width={28}
                            height={28}
                            className="w-7 h-7"
                          />
                        </a>
                      )
                    ))}
                  </div>
                </div>

                {/* Informações de Contato */}
                <ul className="text-sm md:text-base font-normal space-y-3">
                  {contact?.map((item, index) => (
                    <li key={index} className="leading-relaxed text-white/90 hover:text-white transition-colors">
                      {item?.label}
                    </li>
                  ))}
                </ul>
              </div>

              {/* ========== COLUNA 2: MAPA DO SITE ========== */}
              <div className="flex flex-col">
                <h3 className="text-xl md:text-2xl font-semibold mb-6 text-yellow-300">
                  {site_map_title}
                </h3>

                <ul className="flex flex-col space-y-3">
                  {site_map?.map((item, index) => (
                    <li key={index}>
                      {item.external_link ? (
                        <a
                          href={normalizeUrl(item.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-sm md:text-base font-light md:font-normal text-white/90 hover:text-white ${linkHoverStyle}`}
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link
                          href={item.url}
                          className={`text-sm md:text-base font-light md:font-normal text-white/90 hover:text-white ${linkHoverStyle}`}
                        >
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* ========== COLUNA 3: LINKS ÚTEIS ========== */}
              <div className="flex flex-col">
                <h3 className="text-xl md:text-2xl font-semibold mb-6 text-yellow-300">
                  {links_title}
                </h3>

                {/* Redes Sociais - Desktop */}
                <div className="mb-8 hidden md:block">
                  <div className="flex gap-4">
                    {social_midia?.map((item, index) => (
                      item?.logo?.url && (
                        <a
                          key={index}
                          href={normalizeUrl(item.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={item.name}
                          className="transition-transform duration-300 hover:scale-110 flex justify-center items-center border border-white/30 rounded-full py-3 px-3 hover:border-white/60"
                        >
                          <Image
                            src={item.logo.url}
                            alt={item?.name || 'Social media'}
                            width={28}
                            height={28}
                            className="w-7 h-7"
                          />
                        </a>
                      )
                    ))}
                  </div>
                </div>

                {/* Botão Portal Kre+ */}
                {button_kre_mais?.url && (
                  <div>
                    {button_kre_mais.external_link ? (
                      <a
                        href={normalizeUrl(button_kre_mais.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-6 md:px-8 py-2 md:py-3 bg-[#377396] hover:bg-[#377396]/80 text-white font-semibold rounded-full transition-colors duration-300 text-sm md:text-base"
                      >
                        {button_kre_mais.label || 'Portal Kre+'}
                      </a>
                    ) : (
                      <Link
                        href={button_kre_mais.url}
                        className="inline-flex items-center justify-center px-6 md:px-8 py-2 md:py-3 bg-[#377396] hover:bg-[#377396]/80 text-white font-semibold rounded-full transition-colors duration-300 text-sm md:text-base"
                      >
                        {button_kre_mais.label || 'Portal Kre+'}
                      </Link>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* Divisor */}
            <div className="border-t border-white/20 my-8 md:my-12"></div>

            {/* ========== SEÇÃO INFERIOR: PARCEIROS ========== */}
            {cms_parceiros && cms_parceiros.length > 0 && (
              <div className="flex flex-col relative">
                <Marquee className="flex py-3 md:py-4" pauseOnHover={true} repeat={6}>
                  {cms_parceiros.map((partner, index) => (
                    <Link
                      key={index}
                      href={partner.link?.startsWith('http') ? partner.link : `https://${partner.link || '#'}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mx-8 transition-transform duration-300 hover:scale-110"
                    >
                      {partner.logo?.url && (
                        <Image
                          src={partner.logo.url}
                          width={159}
                          height={48}
                          alt={partner.logo.alternativeText || partner.nome}
                          className="w-auto h-auto"
                          unoptimized
                        />
                      )}
                    </Link>
                  ))}
                </Marquee>

                {/* Gradientes de borda para efeito de desvanecimento */}
                <div className="pointer-events-none absolute inset-y-0 left-0 w-1/24 bg-gradient-to-r from-transparent to-transparent"></div>
                <div className="pointer-events-none absolute inset-y-0 right-0 w-1/24 bg-gradient-to-l from-transparent to-transparent"></div>
              </div>
            )}

          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;